import { useTranslation } from './useTranslation';
import { useItemName } from './useItemName';
import { formatQuantity } from '../services/quantityFormatter';
import { parseSellbotReasonItems } from '../services/sellbotReasonItems';

export function useSellbotReason() {
    const { t } = useTranslation();
    const { itemName, stackName, stackSize } = useItemName();

    const reasonItems = (argsJson?: string | null) => {
        const items = parseSellbotReasonItems(argsJson);
        return items.length > 0 ? items : null;
    };
    const fmtMoney = (n: number) => `${new Intl.NumberFormat('de-DE').format(n)}$`;
    // A GG shop item is usually a renamed vanilla one, so the snbt the args carry is the difference
    // between "★ TEAM-Stern ★" and "Netherstern".
    const itemsSummary = (items?: { item: string; count: number; snbt?: string | null }[]) =>
        (items ?? []).map(i => `${formatQuantity(i.count, stackSize(i.item))}x ${itemName(i.item, i.snbt)}`).join(', ');

    const reasonText = (code?: string | null, argsJson?: string | null, fallback?: string | null): string => {
        if (!code) return fallback || '-';
        let a: Record<string, any> = {};
        try { a = argsJson ? JSON.parse(argsJson) : {}; } catch { /* keep empty */ }
        switch (code) {
            case 'sold': return Array.isArray(a.items) && a.items.length > 0
                ? t('SELLBOT_REASON_sold_items', itemsSummary(a.items))
                : t('SELLBOT_REASON_sold', formatQuantity(a.count, stackSize(a.item)), itemName(a.item, a.snbt));
            // The journal step and the transaction row carry their own args, so a row written before
            // the step recorded the amount has to read as a sentence rather than as NaN.
            case 'no_match_amount': return Number.isFinite(a.amount)
                ? t('SELLBOT_REASON_no_match_amount', fmtMoney(a.amount))
                : t('SELLBOT_REASON_no_match');
            case 'offer_gone': return t('SELLBOT_REASON_offer_gone');
            case 'item_disabled': return t('SELLBOT_REASON_item_disabled');
            case 'out_of_stock': return t('SELLBOT_REASON_out_of_stock', formatQuantity(a.needed), formatQuantity(a.available));
            case 'no_inventory_space': return t('SELLBOT_REASON_no_inventory_space', formatQuantity(a.needed), formatQuantity(a.free));
            case 'gather_failed': return t('SELLBOT_REASON_gather_failed', formatQuantity(a.needed));
            case 'partial_delivery': return t('SELLBOT_REASON_partial_delivery', formatQuantity(a.dropped), formatQuantity(a.needed));
            case 'delivery_unverified': return t('SELLBOT_REASON_delivery_unverified', formatQuantity(a.answered), formatQuantity(a.sent));
            case 'failed': return t('SELLBOT_REASON_failed', a.error ?? '');
            case 'ignored_disabled': return t('SELLBOT_REASON_ignored_disabled');
            case 'ignored_occupancy': return t('SELLBOT_REASON_ignored_occupancy');
            case 'payer_mismatch': return t('SELLBOT_REASON_payer_mismatch', a.payer ?? '', a.occupant ?? '');
            case 'payer_mismatch_at_drop': return t('SELLBOT_REASON_payer_mismatch_at_drop', a.expected ?? '', a.actual ?? '', itemsSummary(a.items));
            case 'payer_left_at_drop': return t('SELLBOT_REASON_payer_left_at_drop', a.expected ?? '', itemsSummary(a.items));
            case 'occupant_changed_at_drop': return t('SELLBOT_REASON_occupant_changed_at_drop', a.expected ?? '', a.actual ?? '', itemsSummary(a.items));
            case 'occupant_left_at_drop': return t('SELLBOT_REASON_occupant_left_at_drop', a.expected ?? '', itemsSummary(a.items));
            case 'occupant_changed_uncollected': return t('SELLBOT_REASON_occupant_changed_uncollected', a.expected ?? '', a.actual ?? '', itemsSummary(a.items));
            case 'occupant_left_uncollected': return t('SELLBOT_REASON_occupant_left_uncollected', a.expected ?? '', itemsSummary(a.items));
            case 'payment_received': return t('SELLBOT_REASON_payment_received', fmtMoney(a.amount));
            case 'refund_payment_failed': return t('SELLBOT_REASON_refund_payment_failed', fmtMoney(a.amount), a.payer ?? '', a.error ?? '');
            case 'quote_issued': return t('SELLBOT_REASON_quote_issued', fmtMoney(a.total), formatQuantity(a.bought), formatQuantity(a.rejected));
            case 'price_query': return t('SELLBOT_REASON_price_query', a.query ?? '', formatQuantity(a.results));
            case 'freebie_granted': return t('SELLBOT_REASON_freebie_granted', fmtMoney(a.amount));
            case 'freebie_no_offer': return t('SELLBOT_REASON_freebie_no_offer', fmtMoney(a.amount));
            // Rows written before the toggle recorded its requester carry no args and keep the bare sentence.
            case 'enabled':
            case 'disabled':
                if (!argsJson) return t('SELLBOT_REASON_' + code);
                return a.requestedBy
                    ? t('SELLBOT_REASON_' + code + '_by', a.requestedBy)
                    : t('SELLBOT_REASON_' + code + '_automatic');
            case 'freebie_limit_reached': return t('SELLBOT_REASON_freebie_limit_reached', formatQuantity(a.count), formatQuantity(a.max));
            case 'grant_redeemed':
            case 'grant_not_in_area':
            case 'grant_offer_gone':
                return t('SELLBOT_REASON_' + code, a.code ?? '', a.label ?? '');
            case 'grant_unknown':
            case 'grant_revoked':
            case 'grant_expired':
            case 'grant_used_up':
            case 'grant_wrong_player':
            case 'grant_already_redeemed':
            case 'grant_failed':
                return t('SELLBOT_REASON_' + code, a.code ?? '');
            case 'grant_sender_throttled': return t('SELLBOT_REASON_grant_sender_throttled', formatQuantity(a.count), formatQuantity(a.minutes));
            case 'drop_path_recorded': return t('SELLBOT_REASON_drop_path_recorded', a.zone ?? '', formatQuantity(a.points));
            case 'drop_path_off_zone': return t('SELLBOT_REASON_drop_path_off_zone', a.zone ?? '', a.x, a.y, a.z);
            default: {
                const key = 'SELLBOT_REASON_' + code;
                const localized = t(key);
                return localized !== key ? localized : (fallback || code);
            }
        }
    };

    return { itemName, stackName, reasonText, reasonItems };
}
